/** @jsx jsx */
import { useRef } from 'react';
import { jsx } from '@emotion/core';
import { Field, FieldProps, Form, FormikProps } from 'formik';
import { Typography } from '@material-ui/core';
import useTranslate from 'hooks/useTranslate';
import {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';
import { FormTextField, TextFieldPopover } from 'components/TextField';
import PageContainer from 'components/PageContainer';
import ActionButtonContainer from 'components/ActionButtonsContainer';
import Title from 'components/Title';
import Button from 'components/Button';
import styles from './styles';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import BackToPrevious from 'components/unified/BackToPrevious';

export interface FormValues {
  emailAddress: string | null;
}

type PropTypes<T extends FormValues> = FormikProps<T>;

export default function EmailVerificationView<T extends FormValues>(
  props: PropTypes<T>,
) {
  const { isValid, isSubmitting } = props;
  const softInquiryRef = useRef<HTMLElement | null>(null);
  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();

  return (
    <Form>
      <PageContainer maxWidth="sm">
        <div css={styles.headingContainer}>
          <Title>{tMd('unified.consumerSearch.emailVerification.title')}</Title>
        </div>
        <div css={styles.subHeadingContainer}>
          {tMd('unified.consumerSearch.emailVerification.subtitle')}
          <span css={styles.enquiryIcon}>
            <HelpOutlineIcon fontSize={'inherit'} innerRef={softInquiryRef} />
          </span>

          <TextFieldPopover id="softInquiry" textFieldRef={softInquiryRef}>
            <Typography variant="body2" component="div">
              {tMdMultiline(
                'unified.consumerSearch.emailVerification.softInquiry',
              )}
            </Typography>
          </TextFieldPopover>
        </div>
        <div css={styles.emphasizedText}>
          {tMdMultiline(
            'unified.consumerSearch.emailVerification.instructional',
          )}
        </div>

        <div css={styles.fieldContainer}>
          <Field name="emailAddress">
            {(fieldProps: FieldProps) => (
              <FormTextField
                id="emailAddress"
                fieldProps={fieldProps}
                inputProps={{ maxLength: 50 }}
                required={true}
                type="email"
                autoComplete="email"
                label={t('formLabel.emailAddress')}
              />
            )}
          </Field>
        </div>

        <ActionButtonContainer>
          <Button
            mobileFullWidth={true}
            type="submit"
            id="formSubmit"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
          >
            {tMd('unified.consumerSearch.emailVerification.continueButton')}
          </Button>

          <BackToPrevious tMdKey="unified.consumerSearch.emailVerification.backButton" />
        </ActionButtonContainer>
      </PageContainer>
    </Form>
  );
}
